import { FeatureFlagKey, LocationVarKey } from "@swivl-mono/types";

import { isFeatureEnabled } from "./isFeatureEnabled";

export interface FacilityVarKeys {
  name: LocationVarKey;
  imageURL: LocationVarKey;
  address: LocationVarKey;
  email: LocationVarKey;
  phone: LocationVarKey;
  city: LocationVarKey;
  state: LocationVarKey;
  postalCode: LocationVarKey;
  countryCode: LocationVarKey;
}

export type FacilityVarSet = "cortex" | "legacy";

const CORTEX_KEYS: FacilityVarKeys = {
  name: LocationVarKey.CortexFacilityName,
  imageURL: LocationVarKey.CortexFacilityImageURL,
  address: LocationVarKey.CortexFacilityAddress,
  email: LocationVarKey.CortexFacilityEmail,
  phone: LocationVarKey.CortexFacilityPhone,
  city: LocationVarKey.CortexFacilityCity,
  state: LocationVarKey.CortexFacilityState,
  postalCode: LocationVarKey.CortexFacilityPostalCode,
  countryCode: LocationVarKey.CortexFacilityCountryCode
};

const LEGACY_KEYS: FacilityVarKeys = {
  name: LocationVarKey.Name,
  imageURL: LocationVarKey.AvatarURL,
  address: LocationVarKey.Address,
  email: LocationVarKey.Email,
  phone: LocationVarKey.Phone,
  city: LocationVarKey.City,
  state: LocationVarKey.State,
  postalCode: LocationVarKey.PostalCode,
  countryCode: LocationVarKey.CountryCode
};

/**
 * ENG-3198: the PAGE decides which facility-variable set a component reads —
 * a Snowy page (Properties v3, CC v3, …) reads the `Cortex*` keys its
 * worksheets maintain; a legacy page (legacy PM, CRM, legacy Contact Center,
 * Escalation History) reads the legacy keys the legacy PM maintains. Never
 * pick by data presence (no fallbacks) and never by viewer on a page-scoped
 * component. The ENG-2723 sweep ignored this and blanked every non-migrated
 * org's legacy UI.
 */
export function facilityVarKeysFor(varSet: FacilityVarSet): FacilityVarKeys {
  return varSet === "cortex" ? CORTEX_KEYS : LEGACY_KEYS;
}

/**
 * Flag-resolved variant for app CHROME that belongs to no page's stack —
 * today that is exactly the NavDrawer org switcher. Everything page-scoped
 * must use `facilityVarKeysFor` with the page's own stack instead. Note the
 * Snowy flag is threshold-by-account-kind, so this resolves per VIEWER: an
 * org flagged Snowy but never Cortex-migrated renders blank chrome for staff
 * viewers — that is a data error on whoever flipped the flag without running
 * the migrator (Chuck, 2026-09-02).
 */
export function facilityVarKeys(): FacilityVarKeys {
  return facilityVarKeysFor(isFeatureEnabled(FeatureFlagKey.Snowy) ? "cortex" : "legacy");
}
